import { resultAverageSpeed } from '../models/result-table.models';
import { calculateCategoryStandings } from '../models/category-standings.models';
import { CourseStatisticResult } from '../models/course-statistics.models';
import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Result, Race, Event, normalizeLocation } from '../models/data.models';
import {
    CategoryStanding,
} from '../models/category-standings.models';
import { resolveResultStatus } from '../models/result-status.models';
import { withCategoryStanding } from '../models/podium.models';

/**
 * Payload of the get_club_profile_page RPC. Entities repeated across results are sent once, in
 * dictionaries, and each result is a short array pointing into them by position (see the
 * compact_club_profile_page migration).
 */
interface ClubProfilePagePayload {
    seasons: number[];
    events: [id: string, name: string | null, date: string | null][];
    races: [id: string, eventIndex: number, name: string | null, distanceKm: number | null, discipline: string | null][];
    teams: [id: string, humanIndex: number, dogIndex: number | null][];
    humans: [id: string, name: string | null, gender: string | null][];
    dogs: [id: string, breed: string | null][];
    rows: [raceIndex: number, teamIndex: number, timeSeconds: number | null, category: string | null,
        categoryRank: number | null, discipline: string | null][];
}

export interface ClubProfilePage {
    /** Start year of every sporting season the club has results in, most recent first. */
    seasons: number[];
    results: Result[];
}

@Injectable({
    providedIn: 'root'
})
export class ResultService {
    private supabase = inject(SupabaseService);

    /** Minimal, paginated data for statistics; restricted to displayed races. */
    async getCourseStatisticResults(raceIds: string[]): Promise<CourseStatisticResult[]> {
        const results: CourseStatisticResult[] = [];
        const ids = [...new Set(raceIds)];
        for (let batch = 0; batch < ids.length; batch += 100) {
            for (let offset = 0; ; offset += 1000) {
                const { data, error } = await this.supabase.client.from('results')
                    .select('id,race_id,discipline,category,team:teams(human_id)')
                    .in('race_id', ids.slice(batch, batch + 100))
                    .order('id').range(offset, offset + 999);
                if (error) throw error;
                for (const row of data ?? []) {
                    const team = Array.isArray(row.team) ? row.team[0] : row.team;
                    results.push({ id: row.id, raceId: row.race_id, humanId: team?.human_id ?? null,
                        discipline: row.discipline, category: row.category });
                }
                if (!data || data.length < 1000) break;
            }
        }
        return results;
    }

    /**
     * Fetch results for an EVENT (all races in that event)
     */
    async getResultsByEvent(eventId: string): Promise<Result[]> {
        const rows: any[] = [];
        const pageSize = 1000;
        for (let offset = 0; ; offset += pageSize) {
            const { data, error } = await this.supabase.client
                .rpc('get_event_results_light', { p_event_id: eventId })
                .order('result_id').range(offset, offset + pageSize - 1);
            if (error) throw error;
            rows.push(...(data || []));
            if (!data || data.length < pageSize) break;
        }
        return rows.map((row: any) => this.mapFlatResultRow(row));
    }

    /** Best result of every race of the given events, with participant counts (dashboard). */
    async getDashboardRaceWinners(eventIds: string[]): Promise<{
        winners: Map<string, DashboardRaceWinner[]>;
        participantCounts: Map<string, number>;
    }> {
        const winners = new Map<string, DashboardRaceWinner[]>();
        const participantCounts = new Map<string, number>();
        if (eventIds.length === 0) return { winners, participantCounts };

        const { data, error } = await this.supabase.client.rpc('get_dashboard_race_winners', {
            p_event_ids: eventIds,
        });
        if (error) throw error;

        for (const row of (data || []) as DashboardRaceWinnerRow[]) {
            participantCounts.set(row.event_id, Number(row.event_participant_count));
            const eventWinners = winners.get(row.event_id) ?? [];
            eventWinners.push({
                raceId: row.race_id,
                raceName: row.race_name,
                distanceKm: row.race_distance_km == null ? null : Number(row.race_distance_km),
                discipline: row.race_discipline,
                participantCount: Number(row.race_participant_count),
                timeSeconds: row.time_seconds,
                humanId: row.human_id,
                humanName: row.human_name,
            });
            winners.set(row.event_id, eventWinners);
        }
        return { winners, participantCounts };
    }

    async getResultsByHuman(humanId: string): Promise<(Result & { race: Race })[]> {
        const { data: results, error: err } = await this.supabase.client.rpc('get_profile_results', {
            p_human_id: humanId,
        });

        if (err) throw err;
        return (results || []).map((row: any) => this.mapFlatResultRow(row) as Result & { race: Race });
    }

    async getResultsByDog(dogId: string): Promise<(Result & { race: Race })[]> {
        const { data: results, error: err } = await this.supabase.client.rpc('get_profile_results', {
            p_dog_id: dogId,
        });

        if (err) throw err;
        return (results || []).map((row: any) => this.mapFlatResultRow(row) as Result & { race: Race });
    }

    /**
     * Results of one club for the club profile page, in a single call. The database ranks every
     * participant of the scope's races and only returns the club's own rows, restricted to the
     * columns the page reads. `season` is the start year of a sporting season, null for all of them.
     */
    async getClubProfilePage(clubName: string, season: number | null): Promise<ClubProfilePage> {
        const { data, error } = await this.supabase.client.rpc('get_club_profile_page', {
            p_club_name: clubName,
            p_season: season,
        });
        if (error) throw error;
        const page = data as ClubProfilePagePayload | null;
        if (!page) return { seasons: [], results: [] };

        const scope = season ?? 'all';
        const results = page.rows.map(([raceIndex, teamIndex, timeSeconds, category, categoryRank, discipline], index) => {
            const [raceId, eventIndex, raceName, distanceKm, raceDiscipline] = page.races[raceIndex];
            const [eventId, eventName, eventDate] = page.events[eventIndex];
            const [teamId, humanIndex, dogIndex] = page.teams[teamIndex];
            const [humanId, humanName, humanGender] = page.humans[humanIndex];
            const dog = dogIndex == null ? undefined : page.dogs[dogIndex];
            // category_rank already holds the standing computed from every participant.
            return withCategoryStanding(this.mapFlatResultRow({
                // The page never refers to a single result: a positional id replaces the UUID.
                result_id: `${scope}:${index}`,
                race_id: raceId,
                event_id: eventId,
                team_id: teamId,
                time_seconds: timeSeconds,
                category,
                category_rank: categoryRank,
                discipline,
                race_name: raceName,
                race_distance_km: distanceKm,
                race_discipline: raceDiscipline,
                human_id: humanId,
                human_name: humanName,
                human_gender: humanGender,
                dog_id: dog?.[0],
                dog_breed: dog?.[1],
                event_name: eventName,
                event_date: eventDate,
            }));
        });
        return { seasons: page.seasons, results };
    }

    private mapFlatResultRow(row: any): Result {
        const event = row.event_id ? {
            id: row.event_id,
            name: row.event_name,
            location: row.event_location,
            date: row.event_date,
            weather: row.event_weather,
            description: row.event_description,
            image_url: row.event_image_url,
            region: row.event_region,
            status: row.event_status,
            is_published: row.event_is_published,
            city: row.event_city,
            postal_code: row.event_postal_code,
            department_code: row.event_department_code,
            insee_code: row.event_insee_code,
            latitude: row.event_latitude,
            longitude: row.event_longitude,
            address: row.event_address,
            geo_precision: row.event_geo_precision,
            geo_source: row.event_geo_source,
            geo_confidence: row.event_geo_confidence,
        } : undefined;

        return this.mapRowToResult({
            id: row.result_id,
            race_id: row.race_id,
            team_id: row.team_id,
            time_seconds: row.time_seconds,
            position: row.result_position,
            category: row.category,
            category_rank: row.category_rank,
            bib_number: row.bib_number,
            discipline: row.discipline,
            average_speed_kmph: row.average_speed_kmph,
            source_type: row.source_type,
            source_record_key: row.source_record_key,
            source_url: row.source_url,
            source_metadata: row.source_metadata,
            race: row.race_id ? {
                id: row.race_id,
                event_id: row.event_id,
                name: row.race_name,
                distance_km: row.race_distance_km,
                discipline: row.race_discipline,
                terrain: row.race_terrain,
                event,
            } : undefined,
            team: row.team_id ? {
                id: row.team_id,
                team_name: row.team_name,
                human_id: row.human_id,
                dog_id: row.dog_id,
                human: row.human_id ? {
                    id: row.human_id,
                    name: row.human_name,
                    age: row.human_age,
                    gender: row.human_gender,
                    nationality: row.human_nationality,
                    image_url: row.human_image_url,
                    club: row.human_club ? { name: row.human_club } : undefined,
                } : undefined,
                dog: row.dog_id ? {
                    id: row.dog_id,
                    owner_id: row.dog_owner_id,
                    name: row.dog_name,
                    age: row.dog_age,
                    breed: row.dog_breed,
                    gender: row.dog_gender,
                    image_url: row.dog_image_url,
                } : undefined,
            } : undefined,
        });
    }

    private mapRowToEvent(e: any): Event {
        return {
            id: e.id,
            name: e.name,
            location: normalizeLocation(e.location),
            date: e.date,
            weather: e.weather,
            description: e.description,
            imageUrl: e.image_url,
            region: e.region,
            organizingClub: e.organizing_club,
            status: e.status,
            isPublished: e.is_published
        };
    }

    private mapRowToResult(row: any): Result {
        const resultStatus = resolveResultStatus({
            timeSeconds: row.time_seconds,
            rawTime: row.raw_time ?? row.time_str,
            sourceMetadata: row.source_metadata,
            assumeNotTimed: row.assume_not_timed === true,
        });
        return {
            id: row.id,
            raceId: row.race_id,
            eventId: row.race?.event_id, 
            teamId: row.team_id,
            timeSeconds: row.time_seconds,
            position: row.position,
            category: row.category,
            categoryRank: row.category_rank,
            rank: row.category_rank ?? row.position,
            bibNumber: row.bib_number,
            discipline: row.race?.discipline || row.discipline,
            // Actually 'discipline' is in 'races' now, 'results' also has it in my seed logic but strictly it should be in race?
            // User requested DB update. My migration moved 'discipline' to 'races'.
            // Did it remove it from 'results'? No, I didn't remove it from results table in migration.
            // But good practice is to rely on 'race.discipline'.
            // However, existing code expects it on result.
            averageSpeedKmph: row.average_speed_kmph,
            sourceType: row.source_type,
            sourceRecordKey: row.source_record_key,
            sourceUrl: row.source_url,
            sourceMetadata: row.source_metadata,
            statusCode: resultStatus?.code,
            statusLabel: resultStatus?.label,
            statusRaw: resultStatus?.raw,
            team: row.team ? {
                id: row.team.id,
                teamName: row.team.team_name,
                humanId: row.team.human_id,
                dogId: row.team.dog_id,
                human: row.team.human ? {
                    id: row.team.human.id,
                    name: row.team.human.name,
                    age: row.team.human.age,
                    gender: row.team.human.gender,
                    club: row.team.human.club?.name ?? row.team.human.club,
                    nationality: row.team.human.nationality,
                    imageUrl: row.team.human.image_url
                } : undefined,
                dog: row.team.dog ? {
                    id: row.team.dog.id,
                    ownerId: row.team.dog.owner_id,
                    name: row.team.dog.name,
                    age: row.team.dog.age,
                    breed: row.team.dog.breed,
                    gender: row.team.dog.gender,
                    imageUrl: row.team.dog.image_url
                } : undefined
            } : undefined,
            federation: row.federation || 'FFSLC',
            points: row.points,
            race: row.race ? {
                id: row.race.id,
                eventId: row.race.event_id,
                name: row.race.name,
                distanceKm: row.race.distance_km,
                terrain: row.race.terrain,
                discipline: row.race.discipline
            } : undefined,
            event: row.race?.event ? this.mapRowToEvent(row.race.event) : undefined
        } as Result;
    }

    async updateResult(id: string, updates: Partial<Result>): Promise<void> {
        const dbUpdates: any = {};
        if (updates.timeSeconds !== undefined) dbUpdates.time_seconds = updates.timeSeconds;
        if (updates.position !== undefined) dbUpdates.position = updates.position;
        if (updates.category !== undefined) dbUpdates.category = updates.category;
        if (updates.categoryRank !== undefined) dbUpdates.category_rank = updates.categoryRank;
        if (updates.bibNumber !== undefined) dbUpdates.bib_number = updates.bibNumber;
        // Note: we don't update race_id or team_id via this generic update typically, unless moving results.

        if (Object.keys(dbUpdates).length === 0) return;

        const { error } = await this.supabase.client
            .from('results')
            .update(dbUpdates)
            .eq('id', id);

        if (error) {
            console.error('Error updating result:', error);
            throw error;
        }
    }

    async deleteResult(id: string): Promise<void> {
        const { error } = await this.supabase.client
            .from('results')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting result:', error);
            throw error;
        }
    }

    async getRaceParticipantCounts(raceIds: string[]): Promise<Record<string, number>> {
        if (raceIds.length === 0) return {};

        const { data, error } = await this.supabase.client.rpc('get_race_participant_counts', {
            p_race_ids: raceIds,
        });
        if (error) throw error;
        return Object.fromEntries((data || []).map((row: any) => [row.race_id, Number(row.participant_count)]));
    }

    /**
     * Returns the category rank and field size for every result in the supplied
     * races. This fills category_rank gaps left by historical and partial imports.
     */
    async getCategoryStandings(raceIds: string[]): Promise<Record<string, CategoryStanding>> {
        if (raceIds.length === 0) return {};

        const rows: any[] = [];
        const pageSize = 1000;
        for (let offset = 0; ; offset += pageSize) {
            const { data, error } = await this.supabase.client.rpc('get_category_standings', {
                p_race_ids: [...new Set(raceIds)],
            }).order('result_id').range(offset, offset + pageSize - 1);
            if (error) throw error;
            rows.push(...(data || []));
            if (!data || data.length < pageSize) break;
        }
        return Object.fromEntries(rows.map((row: any) => [row.result_id, {
            rank: row.category_rank == null ? undefined : Number(row.category_rank),
            participantCount: Number(row.participant_count),
        }]));
    }

    private formatTime(seconds: number): string {
        if (!seconds || seconds <= 0) return '—';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return [h, m, s].map(v => v.toString().padStart(2, '0')).join(':');
    }

    async getResultsByEventId(eventId: string): Promise<any[]> {
        const [{ data: races, error: racesError }, eventResults] = await Promise.all([
            this.supabase.client
                .from('races')
                .select('id, name, distance_km, discipline')
                .eq('event_id', eventId),
            this.getResultsByEvent(eventId),
        ]);
        if (racesError) throw racesError;

        const categoryStandings = calculateCategoryStandings(eventResults.map(result => ({
            ...result, category: result.category === '-' ? null : result.category,
            timeSeconds: result.statusCode ? 0 : result.timeSeconds,
            gender: result.team?.human?.gender,
        })));
        const resultsByRace = new Map<string, Result[]>();
        for (const result of eventResults) {
            const rows = resultsByRace.get(result.raceId) ?? [];
            rows.push(result);
            resultsByRace.set(result.raceId, rows);
        }

        return (races || []).map(r => ({
           id: r.id,
           name: r.name,
           distance: r.distance_km ? `${r.distance_km} KM` : '-',
           discipline: r.discipline,
           results: (resultsByRace.get(r.id) || [])
             .slice()
             .sort((a: Result, b: Result) => {
                const aHasTime = (a.timeSeconds ?? 0) > 0;
                const bHasTime = (b.timeSeconds ?? 0) > 0;
                if (aHasTime !== bHasTime) return aHasTime ? -1 : 1;
                if (aHasTime) return a.timeSeconds - b.timeSeconds;
                return (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER);
             })
             .map((res: Result) => ({
                rank: (res.timeSeconds ?? 0) > 0
                  ? res.position?.toString() || '-'
                  : '—',
                federation: res.federation || 'FFSLC',
                license: res.bibNumber || '-',
                humanId: res.team?.human?.id || null,
                dogId: res.team?.dog?.id || null,
                nom: res.team?.human?.name || '-',
                prenom: '',
                categorie: res.category || '-',
                categoryRank: categoryStandings[res.id]?.rank,
                averageSpeedKmph: resultAverageSpeed(r.distance_km, res.timeSeconds, res.statusCode, res.averageSpeedKmph),
                details: `${res.team?.human?.club || ''} ${res.team?.dog?.name || ''}`.trim(),
                temps: this.formatTime(res.timeSeconds),
                points: res.points?.toString() || '-',
                sourceType: res.sourceType,
                sourceRecordKey: res.sourceRecordKey,
                sourceUrl: res.sourceUrl,
                sourceMetadata: res.sourceMetadata,
                statusCode: res.statusCode,
                statusLabel: res.statusLabel,
                statusRaw: res.statusRaw,
             }))
        }));
    }

    async getRankingsSummary(params: {
        region?: string | null;
        discipline?: string | null;
        gender?: string | null;
        category?: string | null;
        seasonStart?: string | null;
    }): Promise<RankingSummaryRow[]> {
        const rows: RankingSummaryRow[] = [];
        const pageSize = 1000;
        for (let offset = 0; ; offset += pageSize) {
            const { data, error } = await this.supabase.client.rpc('get_participation_rankings', {
                p_region: params.region || null,
                p_discipline: params.discipline || null,
                p_gender: params.gender || null,
                p_category: params.category || null,
                p_season_start: params.seasonStart || null,
            }).order('human_id').range(offset, offset + pageSize - 1);
            if (error) throw error;
            rows.push(...(data || []) as RankingSummaryRow[]);
            if (!data || data.length < pageSize) break;
        }
        return rows;
    }

    async getRankingsFacets(params: {
        region?: string | null;
        discipline?: string | null;
        gender?: string | null;
        category?: string | null;
        seasonStart?: string | null;
    }): Promise<RankingsFacetRow[]> {
        const { data, error } = await this.supabase.client.rpc('get_rankings_facets', {
            p_region: params.region || null,
            p_discipline: params.discipline || null,
            p_gender: params.gender || null,
            p_category: params.category || null,
            p_season_start: params.seasonStart || null,
        });
        if (error) throw error;
        return (data || []) as RankingsFacetRow[];
    }
}

interface DashboardRaceWinnerRow {
    event_id: string;
    event_participant_count: number | string;
    race_id: string;
    race_name: string;
    race_distance_km: number | string | null;
    race_discipline: string | null;
    race_participant_count: number | string;
    result_id: string;
    time_seconds: number | null;
    human_id: string | null;
    human_name: string | null;
}

export interface DashboardRaceWinner {
    raceId: string;
    raceName: string;
    distanceKm: number | null;
    discipline: string | null;
    participantCount: number;
    /** Null or zero when nobody finished the race: there is no winner to show. */
    timeSeconds: number | null;
    humanId: string | null;
    humanName: string | null;
}

export interface RankingSummaryRow {
    human_id: string;
    human_name: string;
    human_age: number;
    human_gender: string;
    human_club: string;
    human_nationality: string;
    human_image_url: string;
    medals_first: number;
    medals_second: number;
    medals_third: number;
    medals_fourth: number;
    race_count: number;
    total_distance_km: number;
    total_time_seconds: number;
}

export interface RankingsFacetRow {
    facet_type: 'breed' | 'discipline' | string;
    facet_name: string;
    facet_count: number;
    total_results: number;
}
